import { useRef, useSyncExternalStore } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  updateEditablePreviewCells,
  resetEditablePreviewOverrides,
  addEditablePreviewTag,
  removeEditablePreviewTag,
  reorderTag,
} from "@/apis/editable-preview/route";
import {
  AddStructuralTagRequest,
  EditableTableItemsV2,
  EditableTablePreviewResponseV2,
  ReorderTagRequest,
  UpdateCellValueRequest,
} from "@/apis/editable-preview/types";
import queries from "@/queries";
import { AsyncQueuer } from "@tanstack/pacer";
import { toast } from "sonner";
import { useWorkbookIdAndTableId } from "../hook";
import { TreeViewData, useNotUsedSumTagKeys } from "./hook";
import { XBRLTreeViewResponse } from "@/apis/xbrl-view/types";

export const useEditablePreview = (isEditMode: boolean) => {
  const {
    data: { workbookId, tableId },
  } = useWorkbookIdAndTableId();
  const { data: notUsedSumTagKeysData } = useNotUsedSumTagKeys();

  const {
    isLoading,
    isFetching,
    error,
    data: editablePreview,
  } = useQuery({
    ...queries
      .workbook(workbookId!)
      ._ctx.tables(tableId!)
      ._ctx.editablePreview()
      ._ctx.withQueryParam(notUsedSumTagKeysData?.notUsedSumTagKeys ?? []),
    enabled: !!workbookId && !!tableId && !!notUsedSumTagKeysData && isEditMode,
    gcTime: 10 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  if (editablePreview?.current.tables.length === 0) {
    return {
      editablePreview: null,
      isLoading: false,
      isFetching: false,
      error: null,
    };
  }

  return {
    editablePreview,
    isLoading,
    isFetching,
    error,
  };
};

export const useEditableTreeView = ({ enabled }: { enabled: boolean }) => {
  const {
    data: { workbookId, tableId },
  } = useWorkbookIdAndTableId();

  const {
    isLoading,
    error,
    data: tableTreeView,
  } = useQuery({
    ...queries
      .workbook(workbookId!)
      ._ctx.tables(tableId!)
      ._ctx.editableTreeView(),
    select: selectTreeViewData,
    enabled: !!workbookId && !!tableId && enabled,
    gcTime: 10 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  return { tableTreeView, isLoading, error };
};

const invalidateEditableQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  workbookId: string,
  tableId: string,
) => {
  const tableCtx = queries.workbook(workbookId)._ctx.tables(tableId);
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: tableCtx._ctx.editablePreview().queryKey,
    }),
    queryClient.invalidateQueries({
      queryKey: tableCtx._ctx.editableTreeView().queryKey,
    }),
  ]);
};

// 순서 변경 → 저장 → preview refetch 완료까지의 "반영중" 상태를 구독 가능한 전역 store.
// reorder queue가 싱글톤이라 상태도 모듈 레벨에 둔다.
let isReflecting = false;
let reflectGen = 0; // refetch 도중 새 reorder가 들어오면 false 처리를 건너뛰기 위한 세대 카운터
const reflectingListeners = new Set<() => void>();
const setReflecting = (next: boolean) => {
  if (isReflecting === next) return;
  isReflecting = next;
  reflectingListeners.forEach((listener) => listener());
};

export const useIsReflecting = () =>
  useSyncExternalStore(
    (onChange) => {
      reflectingListeners.add(onChange);
      return () => reflectingListeners.delete(onChange);
    },
    () => isReflecting,
  );

function updateTablesByYear(
  tables: EditableTableItemsV2[],
  updates: UpdateCellValueRequest[],
  targetYear: "CURRENT" | "PREVIOUS",
): EditableTableItemsV2[] {
  const relevantUpdates = updates.filter((u) => u.targetYear === targetYear);
  if (relevantUpdates.length === 0) return tables;

  const updateMap = new Map(
    relevantUpdates.map((u) => [`${u.roleKey}::${u.cellTagKey}`, u]),
  );

  return tables.map((table) => ({
    ...table,
    rowItems: table.rowItems.map((row) =>
      row.map((item) => {
        // TODO: Replace this td-only guard once PreviewRowItem separates display tag ids from editable cell keys.
        // PreviewRowItem.id can currently collide across th/td, so optimistic updates must ignore headers.
        if (item.type !== "td") return item;
        const update = updateMap.get(`${table.roleKey}::${item.id}`);
        if (!update) return item;
        return {
          ...item,
          value: update.value,
          isEdited: update.value !== item.originalValue,
          // originalValue는 변경하지 않음 (최초 원본값 보존)
        };
      }),
    ),
  }));
}

function applyOptimisticUpdate(
  data: EditableTablePreviewResponseV2,
  updates: UpdateCellValueRequest[],
): EditableTablePreviewResponseV2 {
  return {
    ...data,
    current: {
      ...data.current,
      tables: updateTablesByYear(data.current.tables, updates, "CURRENT"),
    },
    prior: {
      ...data.prior,
      tables: updateTablesByYear(data.prior.tables, updates, "PREVIOUS"),
    },
  };
}

const CELL_UPDATE_DEBOUNCE_MS = 2000;

export const useUpdateCellValue = () => {
  const {
    data: { workbookId, tableId },
  } = useWorkbookIdAndTableId();
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const editablePreviewQueryKey = queries
    .workbook(workbookId!)
    ._ctx.tables(tableId!)
    ._ctx.editablePreview().queryKey;

  return useMutation({
    mutationFn: (updates: UpdateCellValueRequest[]) =>
      updateEditablePreviewCells(workbookId!, tableId!, { updates }),

    onMutate: async (updates) => {
      // 진행 중인 fetch 취소 (race condition 방지)
      await queryClient.cancelQueries({ queryKey: editablePreviewQueryKey });

      // 롤백용 스냅샷 저장 (notIsSumMemberTags 조합별 캐시 인스턴스 포함)
      const snapshots =
        queryClient.getQueriesData<EditableTablePreviewResponseV2>({
          queryKey: editablePreviewQueryKey,
        });

      // 모든 캐시 인스턴스에 optimistic update 즉시 적용
      queryClient.setQueriesData<EditableTablePreviewResponseV2>(
        { queryKey: editablePreviewQueryKey },
        (old) => (old ? applyOptimisticUpdate(old, updates) : old),
      );

      return { snapshots };
    },

    onError: (_error, _variables, context) => {
      // 스냅샷으로 이전 상태 롤백
      context?.snapshots.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },

    onSuccess: () => {
      // 3초 debounce: 연속 편집 시 마지막 성공 후 한 번만 re-fetch
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: editablePreviewQueryKey });
        debounceTimerRef.current = null;
      }, CELL_UPDATE_DEBOUNCE_MS);
    },
  });
};

export const useResetOverrides = () => {
  const {
    data: { workbookId, tableId },
  } = useWorkbookIdAndTableId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roleKey?: string) => {
      return resetEditablePreviewOverrides(workbookId!, tableId!, roleKey);
    },
    onSuccess: () => {
      invalidateEditableQueries(queryClient, workbookId!, tableId!);
    },
  });
};

export const useAddStructuralTag = () => {
  const {
    data: { workbookId, tableId },
  } = useWorkbookIdAndTableId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: AddStructuralTagRequest) => {
      return addEditablePreviewTag(workbookId!, tableId!, request);
    },
    onSuccess: () => {
      invalidateEditableQueries(queryClient, workbookId!, tableId!);
    },
  });
};

export const useRemoveStructuralTag = () => {
  const {
    data: { workbookId, tableId },
  } = useWorkbookIdAndTableId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tagKey, roleKey }: { tagKey: string; roleKey: string }) => {
      return removeEditablePreviewTag(workbookId!, tableId!, tagKey, roleKey);
    },
    onSuccess: () => {
      invalidateEditableQueries(queryClient, workbookId!, tableId!);
    },
  });
};

// reorder 요청을 순차 처리하기 위한 전역 queue (싱글톤)
interface ReorderQueueItem {
  workbookId: string;
  tableId: string;
  request: ReorderTagRequest;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

let reorderQueueInstance: AsyncQueuer<ReorderQueueItem> | null = null;

const getReorderTagQueue = () => {
  if (reorderQueueInstance) return reorderQueueInstance;

  reorderQueueInstance = new AsyncQueuer<ReorderQueueItem>(
    async (item) => {
      await reorderTag(item.workbookId, item.tableId, item.request);
    },
    {
      concurrency: 1, // 순차 처리
      onSuccess: (_result, item) => item.onSuccess?.(),
      onError: (error, item) => item.onError?.(error as Error),
    },
  );
  reorderQueueInstance.start();
  return reorderQueueInstance;
};

export const useReorderTag = () => {
  const {
    data: { workbookId, tableId },
  } = useWorkbookIdAndTableId();
  const queryClient = useQueryClient();
  const queue = getReorderTagQueue();
  const invalidateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reorderTagMutate = (
    request: ReorderTagRequest,
    options?: { onError?: () => void },
  ) => {
    if (workbookId === null || tableId === null) {
      console.error("Workbook ID or Table ID is not found");
      return;
    }

    // 순서 변경 시작 → refetch 완료까지 "반영중" 표시
    setReflecting(true);
    reflectGen += 1;

    // 진행 중인 refetch를 취소하여 optimistic update를 덮어쓰지 않도록 함
    const tableCtx = queries.workbook(workbookId)._ctx.tables(tableId);
    queryClient.cancelQueries({
      queryKey: tableCtx._ctx.editablePreview().queryKey,
    });
    queryClient.cancelQueries({
      queryKey: tableCtx._ctx.editableTreeView().queryKey,
    });

    queue.addItem({
      workbookId,
      tableId,
      request,
      onSuccess: () => {
        // 마지막 요청 처리 후 2초간 추가 요청이 없으면 invalidate (debounce)
        if (invalidateTimer.current) clearTimeout(invalidateTimer.current);
        invalidateTimer.current = setTimeout(async () => {
          const gen = reflectGen;
          await invalidateEditableQueries(queryClient, workbookId, tableId);
          // refetch 도중 새 reorder가 들어왔거나 아직 처리할 요청이 남았으면 유지
          if (gen === reflectGen && queue.peekPendingItems().length === 0) {
            setReflecting(false);
          }
        }, 2000);
      },
      onError: () => {
        // 에러 발생 시 이후의 모든 pending 요청 취소
        if (invalidateTimer.current) clearTimeout(invalidateTimer.current);
        queue.clear();
        options?.onError?.();
        toast.error("이동할 수 없습니다.", { duration: 3000 });
        // 에러 발생 시 서버 데이터와 동기화
        invalidateEditableQueries(queryClient, workbookId, tableId);
        setReflecting(false);
      },
    });
  };

  return { mutate: reorderTagMutate };
};
export const selectTreeViewData = (
  response: XBRLTreeViewResponse,
): TreeViewData => ({
  items: response.treeview.tables.map((t) => ({
    ...t,
    isExtended: t.isExtended ?? false,
    lineItem: t.lineItem.map((i) => ({
      ...i,
      isExtendedTag: i.isExtendedTag ?? false,
      isSum: i.isSum ?? false,
      isStructuralOverride: i.isStructuralOverride ?? false,
      autoAdded: i.autoAdded ?? false,
    })),
    axis: t.axis.map((i) => ({
      ...i,
      isExtendedTag: i.isExtendedTag ?? false,
      isSum: i.isSum ?? false,
      isStructuralOverride: i.isStructuralOverride ?? false,
      autoAdded: i.autoAdded ?? false,
    })),
  })),
  blockItems: response.blockItems,
});
